/**
 * 🛡️ SENTRY ERROR TRACKING v3.0 — lazy SDK
 * Professional error monitoring with source maps.
 *
 * The @sentry/react SDK (~271 kB raw) is loaded INSIDE init(), never statically —
 * keeping it out of the boot-critical bundle. init() is scheduled post-paint by
 * app-main.ts. Errors that happen before init are queued in window.__bootErrors
 * (see app-main.ts) and flushed here once the SDK is up.
 *
 * Direct `import * as Sentry from '@sentry/react'` is forbidden outside this file
 * (eslint no-restricted-imports).
 */

import type { SeverityLevel } from '@sentry/react';

// Injected by Vite define() from COMMIT_REF — same value the source-map uploader uses.
declare const __APP_RELEASE__: string;
import { performanceMonitor } from '../monitoring/performanceMonitor';
import { isChunkLoadError } from './chunkErrorHandler';
import { Logger } from '@theidiomaclub/logging';
import { registry } from './registry';

// Populated by init() via dynamic import. Every call-site below must go through
// this nullable reference — a static import would drag the SDK back into wave 1.
let SentryModule: typeof import('@sentry/react') | null = null;

declare global {
    interface Window {
        Sentry?: {
            captureException: (error: unknown) => void;
            captureMessage: (msg: string) => void;
            setUser: (user: unknown) => void;
            addBreadcrumb: (breadcrumb: unknown) => void;
        };
    }
}

export interface SentryConfig {
    dsn: string;
    environment: 'development' | 'production' | 'staging';
    release?: string;
    tracesSampleRate: number;
    replaysSessionSampleRate: number;
    replaysOnErrorSampleRate: number;
}

class SentryService {
    private initialized = false;
    private initPromise: Promise<void> | null = null;
    private hashChangeController: AbortController | null = null;

    /**
     * Initialize Sentry with environment detection.
     * Async: lazy-loads the SDK first. Safe to call multiple times.
     */
    init(config?: Partial<SentryConfig>): Promise<void> {
        if (this.initPromise) return this.initPromise;
        this.initPromise = this.doInit(config).catch((err: unknown) => {
            // Fail loud in logs, never in UX — the app must work without Sentry.
            Logger.warn('Sentry SDK failed to load — error tracking disabled', {
                action: 'sentry_sdk_load_failed',
                error: err instanceof Error ? err.message : String(err),
            });
        });
        return this.initPromise;
    }

    private async doInit(config?: Partial<SentryConfig>): Promise<void> {
        if (this.initialized) {
            Logger.warn('Sentry already initialized');
            return;
        }

        // Get DSN from env or config
        const dsn = config?.dsn || import.meta.env.VITE_SENTRY_DSN;

        const isInvalidDsn = !dsn ||
            typeof dsn !== 'string' ||
            dsn.trim() === '' ||
            dsn.includes('placeholder') ||
            dsn.includes('your-sentry-dsn') ||
            dsn.includes('project-id');

        if (isInvalidDsn) {
            Logger.system('Sentry: Skipped (no valid DSN configured)');
            this.setupMockSentry();
            return;
        }

        // Lazy-load the SDK — this is what keeps ~271 kB out of the boot bundle.
        SentryModule = await import('@sentry/react');
        const Sentry = SentryModule;

        const environment = this.getEnvironment();
        const release = this.getRelease();

        // ⚡ PERF FIX: Initialize Sentry core immediately (error capture, breadcrumbs)
        // but defer browserTracingIntegration until idle.
        Sentry.init({
            dsn,
            environment,
            release,

            // Route reports through our own domain (see netlify.toml /monitoring redirect).
            // Only in non-dev: /monitoring doesn't exist locally and would silently drop events.
            ...(environment !== 'development' && { tunnel: '/monitoring' }),

            // Performance Monitoring — 10% in prod, 0 in dev (1.0 causes quota blowout)
            tracesSampleRate: config?.tracesSampleRate ?? (environment === 'production' ? 0.1 : 0.0),

            // Session Replay — 10% of sessions (errors always at 100% via replaysOnErrorSampleRate)
            replaysSessionSampleRate: config?.replaysSessionSampleRate ?? 0.1,
            replaysOnErrorSampleRate: config?.replaysOnErrorSampleRate ?? 1.0,

            // Integrations: NO browserTracingIntegration at boot — added lazily below
            integrations: [],

            // SDK-level noise filter — skipped before event construction, cheapest possible gate.
            ignoreErrors: ['Navigator LockManager', 'Lock broken by another request'],

            // Error filtering
            beforeSend(event, hint) {
                // Chunk-load errors (stale deploy): suppress before any other check.
                // chunkErrorHandler already handles auto-reload; second failures reach Sentry
                // intentionally via the global unhandledrejection handler below.
                if (isChunkLoadError(hint.originalException)) return null;

                // Don't send errors in development
                if (environment === 'development') {
                    Logger.debug('Sentry Dev', event, hint);
                    return null;
                }

                // Filter out noisy errors
                const error = hint.originalException;
                if (error instanceof Error) {
                    // Ignore network errors (user offline)
                    if (error.message.includes('Failed to fetch') ||
                        error.message.includes('NetworkError')) {
                        return null;
                    }

                    // Ignore browser extension errors
                    if (error.stack?.includes('chrome-extension://')) {
                        return null;
                    }
                }

                return event;
            },

            // Add custom context
            initialScope: {
                tags: {
                    app_version: release,
                },
            },
        });

        this.initialized = true;
        Logger.system(`Sentry initialized (${environment}) [LAZY SDK]`);

        // Flush errors captured before Sentry was ready (app-main.ts boot guard)
        const bootErrors = (window as unknown as { __bootErrors?: Array<{ message: string; source?: string; lineno?: number }> }).__bootErrors;
        if (bootErrors?.length) {
            bootErrors.forEach(e => {
                Sentry.captureMessage(`[boot] ${e.message}`, { level: 'error', extra: { source: e.source, lineno: e.lineno } });
            });
            bootErrors.length = 0;
        }

        // Integration with performance monitor
        this.setupPerformanceIntegration();

        // ⚡ IDLE TRACING + REPLAY: Add heavy integrations during browser idle time.
        // Replay is loaded for ALL users (students included) — required to debug
        // real-world bugs. Fallback to setTimeout for Safari (no requestIdleCallback).
        if (typeof window !== 'undefined') {
            const addHeavyIntegrations = () => {
                try {
                    SentryModule?.addIntegration(SentryModule.browserTracingIntegration());
                    SentryModule?.addIntegration(SentryModule.replayIntegration({
                        maskAllText: false,
                        blockAllMedia: false,
                    }));
                    Logger.system('Sentry: browserTracing + replay added (idle)');
                } catch { /* already added or unsupported */ }
            };
            if (typeof requestIdleCallback !== 'undefined') {
                requestIdleCallback(addHeavyIntegrations, { timeout: 3000 });
            } else {
                setTimeout(addHeavyIntegrations, 1000);
            }

            // HashRouter route tracking — browserTracingIntegration tracks pushState but not hash changes.
            // This fires a Sentry navigation span on every #/route change so the dashboard
            // shows /map, /lesson/:id, /admin/ etc. instead of everything as "/".
            this.setupHashRouteTracking();
        }
    }

    /**
     * Capture an error with context
     */
    captureError(error: Error, context?: Record<string, unknown>) {
        if (!this.initialized || !SentryModule) {
            Logger.error('Sentry not initialized:', error);
            return;
        }

        SentryModule.withScope((scope) => {
            if (context) {
                scope.setContext('additional', context);
            }
            SentryModule?.captureException(error);
        });
    }

    /**
     * Capture a message (for non-errors)
     */
    captureMessage(message: string, level: SeverityLevel = 'info') {
        if (!this.initialized || !SentryModule) return;
        SentryModule.captureMessage(message, level);
    }

    /**
     * Set user context
     */
    setUser(user: { id: string; email?: string; username?: string } | null) {
        if (!this.initialized || !SentryModule) return;

        SentryModule.setUser(user);
    }

    /**
     * Add breadcrumb (for debugging)
     */
    addBreadcrumb(message: string, category: string = 'action', data?: Record<string, unknown>) {
        if (!this.initialized || !SentryModule) {
            Logger.debug(`[Breadcrumb] ${message}`, data);
            return;
        }

        SentryModule.addBreadcrumb({
            message,
            category,
            ...(data !== undefined && { data }),
            level: 'info',
            timestamp: Date.now() / 1000,
        });
    }

    /**
     * Start a transaction (for performance tracking)
     */
    startTransaction(_name: string, _op: string = 'navigation') {
        if (!this.initialized) return null;

        // startTransaction está deprecated en v8, se usa startSpan o similar
        // Para mantener compatibilidad simple en este flujo:
        return null;
    }

    /**
     * Manually flush events (useful before page unload)
     */
    async flush(timeout: number = 2000): Promise<boolean> {
        if (!this.initialized || !SentryModule) return true;

        return SentryModule.flush(timeout);
    }

    // --- Private Methods ---

    private getEnvironment(): 'development' | 'production' | 'staging' {
        if (import.meta.env.MODE === 'production') return 'production';
        if (import.meta.env.MODE === 'staging') return 'staging';
        return 'development';
    }

    private getRelease(): string {
        // Build-time git commit SHA (from Netlify COMMIT_REF), matching the source-map uploader.
        // This is what makes production stack traces readable instead of minified.
        return typeof __APP_RELEASE__ !== 'undefined' ? __APP_RELEASE__ : 'dev';
    }

    private setupMockSentry() {
        // Mock Sentry for development
        window.Sentry = {
            captureException: (error: unknown) => Logger.debug('[Mock Sentry] Error:', error),
            captureMessage: (msg: string) => Logger.debug('[Mock Sentry] Message:', msg),
            setUser: (user: unknown) => Logger.debug('[Mock Sentry] User:', user),
            addBreadcrumb: (breadcrumb: unknown) => Logger.debug('[Mock Sentry] Breadcrumb:', breadcrumb),
        };
    }

    private setupHashRouteTracking() {
        // Normalize #/map/world/1/lesson/42 → /map/world/:id/lesson/:id
        const normalizeHash = (hash: string): string => {
            const route = hash.replace(/^#\/?/, '/') || '/';
            return route
                .replace(/\/[0-9a-f]{8}-[0-9a-f-]{27}/g, '/:id')   // UUIDs
                .replace(/\/\d+/g, '/:id');                           // numeric ids
        };

        let currentRoute = normalizeHash(window.location.hash);
        SentryModule?.setTag('route', currentRoute);
        SentryModule?.getCurrentScope().setTransactionName(currentRoute);

        this.hashChangeController?.abort();
        this.hashChangeController = new AbortController();
        // NOTE: this closure must read SentryModule (the lazy module ref), never a
        // static import — easiest call-site to miss when the SDK became lazy.
        window.addEventListener('hashchange', () => {
            const nextRoute = normalizeHash(window.location.hash);
            if (nextRoute === currentRoute) return;
            currentRoute = nextRoute;
            SentryModule?.setTag('route', currentRoute);
            // Sets transaction name so Sentry dashboard groups by route, not all as "/"
            SentryModule?.getCurrentScope().setTransactionName(currentRoute);
        }, { signal: this.hashChangeController.signal });
    }

    destroy() {
        this.hashChangeController?.abort();
        this.hashChangeController = null;
        this.initialized = false;
        this.initPromise = null;
    }

    private setupPerformanceIntegration() {
        // Send performance metrics to Sentry every 5 minutes
        setInterval(() => {
            const metrics = performanceMonitor.getMetrics();

            SentryModule?.setContext('performance', {
                batch_effectiveness: metrics.batchEfficiency,
                persist_efficiency: metrics.persistEfficiency,
                avg_update_time: metrics.avgUpdateTime,
                raf_executed: metrics.rafExecuted,
            });

            // Alert if performance degrades
            if (metrics.avgUpdateTime > 50) {
                this.captureMessage(
                    `Performance degradation: ${metrics.avgUpdateTime}ms avg update`,
                    'warning'
                );
            }
        }, 5 * 60 * 1000);
    }
}

// Singleton instance
export const sentry = new SentryService();

// NO module-scope auto-init: init() is scheduled post-paint by app-main.ts.
// Initializing here would drag the SDK into the boot-critical path.

// Register in DI container
if (typeof window !== 'undefined' && registry) {
    registry.register('Sentry', sentry);
}

// Global error handlers — safe pre-init: captureError no-ops (and logs) until init().
if (typeof window !== 'undefined') {
    window.addEventListener('unhandledrejection', (event) => {
        const msg = event.reason instanceof Error ? event.reason.message : String(event.reason);
        // Harmless auth race condition from Supabase GoTrue
        if (msg.includes('Lock broken by another request')) return;
        // Chunk load errors are handled by chunkErrorHandler (auto-reload) — not actionable in Sentry
        // Exception: if chunkErrorHandler already reloaded and it failed again, it won't preventDefault,
        // so that second failure WILL reach Sentry as a real issue worth investigating.
        if (msg.toLowerCase().includes('importing a module script failed') ||
            msg.toLowerCase().includes('failed to fetch dynamically imported module')) return;

        sentry.captureError(
            event.reason instanceof Error ? event.reason : new Error(String(event.reason)),
            { type: 'unhandledRejection' }
        );
    });

    window.addEventListener('error', (event) => {
        sentry.captureError(
            event.error || new Error(event.message),
            {
                type: 'uncaughtError',
                filename: event.filename,
                lineno: event.lineno,
                colno: event.colno,
            }
        );
    });

    // Flush before page unload
    window.addEventListener('beforeunload', () => {
        void sentry.flush(1000);
    });
}

export default sentry;
